import {FontWeight} from '@nolimitcity/slot-launcher/bin/loader/font/FontStatics';
import {TextStyleOptions} from '../../../../core/setting/text/IText';
import {Helper} from '../../../../core/utils/Helper';
import {PunkRocker} from '../../../PunkRocker';
import {GameData} from '../../../server/data/GameData';
import {gsap} from 'gsap';
import {GameAssets} from "../../../config/texture/GameAssets";
import {BitmapText} from "pixi.js";
import {ContinueButton} from "@nolimitcity/slot-launcher/bin/gui/buttons/concretebuttons/ContinueButton";
import {SpinePlayer} from "../../../../core/displayobject/SpinePlayer";
import {BonusGameEndState, NoWinPosterState} from "../Poster/PosterManager";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {SoundManager} from "../../../sound/SoundManager";
import {SoundConfig} from "../../../sound/SoundConfig";
import {GameConfig} from "../../../config/base/GameConfig";
import {GameMode} from "@nolimitcity/slot-game/bin/flowcontrol/GameMode";
import {PREngineManager} from "../../../punkEngine/PREngineManager";

export class FSOutroView extends PIXI.Container {
    private _spinsPlayedContainer: PIXI.Sprite;
    private _spinsPlayedText: PIXI.Text;
    private _spinsPlayedNr: PIXI.Text;
    private _totalWin: BitmapText;
    private _continueButton: ContinueButton;
    private totalWin: number;
    private isBelow: boolean;
    private _timeline: gsap.core.Timeline;
    private _loop: gsap.core.Timeline;
    private _scatterPos: number[] = [];
    private _isCapWin: boolean = false;
    private _spinsPlayedTextStyle: TextStyleOptions = {
        fill: "#fff",
        fontFamily: "Open Sans",
        fontSize: 34,
        fontWeight: FontWeight.EXTRA_BOLD,
        padding: 10,
        stroke: "#000",
        strokeThickness: 8
    };
    private _animation: SpinePlayer; // #334
    private _wasFeatureBuy: boolean; // #339
    private _noWinAnimation: SpinePlayer; // #351

    constructor() {
        super();
        this.name = "FSOutroView";

        this.initAnimations();
    }

    public setData(data: GameData): void {
        this._scatterPos = data.Reel.scatterPositions;
        this._isCapWin = data.raw.brokeTheBank;
        this.totalWin = data.raw.accumulatedRoundWin;
        this.isBelow = data.Win.isWinBelowStake();
        this._spinsPlayedNr.text = data.raw.numberOfFreespinsPlayed.toString();
        this._wasFeatureBuy = data.raw.wasFeatureBuy; // #339
    }

    // #351
    public playNoWinAnimation(): gsap.core.Timeline {
        return this._noWinAnimation.play({
            animation: "animation"
        });
    }

    public showFSOutro(): gsap.core.Timeline {
        if (this._isCapWin) {
            this.clickCallback();
            return gsap.timeline();
        }
        GameConfig.FORCE_BRING_BOTTOM_TRAIN = false;
        PunkRocker.posterManager.forceFadeGradientOverlay();

        this._noWinAnimation.visible = false;

        this._timeline = gsap.timeline();
        this._timeline.add(this.play());

        this._timeline.add(() => {
            this._animation.play({
                animation: "loop",
                loop: true
            });
        }, "-=0.05");

        // #339
        if (this._wasFeatureBuy && PunkRocker.autoPlayPlugin.isAutoplayRound) {
            this._continueButton.visible = false;

            this._timeline.to(this, {
                duration: 1
            });
            this._timeline.add(() => {
                this.clickCallback();
            });
        } else {
            this._continueButton.visible = true;

            this._timeline.fromTo(this._continueButton, {duration: 0.25, alpha: 0}, {alpha: 1});

            this._timeline.add(() => {
                this._continueButton.enable(true);
            });
        }

        this._loop = Helper.setTimeline(this._loop, new gsap.core.Timeline({repeat: -1}));
        this._loop.to(this, {duration: 100});

        return this._loop;
    }

    public play(): gsap.core.Timeline {
        PunkRocker.screenDimmer.show();

        const totalLength: number = this._spinsPlayedText.width + this._spinsPlayedNr.width + 20;
        this._spinsPlayedContainer.position.set(360 - totalLength * 0.5, 435);
        this._spinsPlayedContainer.alpha = 1;
        this._spinsPlayedContainer.visible = true;

        // #310
        for (let reel: number = 0; reel < PREngineManager.AllReels.length; reel++) {
            PREngineManager.getReel(reel).overlayGraphicAlpha(0, 0.3);
            PREngineManager.getReel(reel).showReelGlow(false);
        }

        const tl: gsap.core.Timeline = Helper.setTimeline();
        if (this.totalWin > 0) {
            this._totalWin.alpha = 0;
            this._totalWin.visible = true;

            this._totalWin.text = (this.totalWin.toFixed(2)).toString();
            tl.to(this._totalWin,{alpha: 1, duration: 0.2}, 0);
        }

        tl.add([
            PunkRocker.screenDimmer.fadeIn(),
            // #334
            this._animation.play({
                animation: "intro"
            }),
            () => {
                this._animation.alpha = 1;

                if (!this.isBelow) {
                    SoundManager.playSoundEffect(SoundConfig.FS_OUTRO_EFFECT);
                }
                SoundManager.playScreenMusic(SoundConfig.FS_OUTRO, 1.5);
            }
        ], 0);

        return tl;
    }

    public stop(): gsap.core.Timeline {
        return gsap.timeline();
    }

    public onResize(): void {
        if (NolimitApplication.isLandscape) {
            this._continueButton.position.set(360, 615);
        } else {
            const centerBottom: number = (NolimitApplication.screenBounds.bottom - 610) * 0.5 + 610;
            this._continueButton.position.set(360, centerBottom - 80);
        }
    }

    private initAnimations(): void {
        this._noWinAnimation = new SpinePlayer(NoWinPosterState.NO_WIN, false);
        this._noWinAnimation.position.set(360);
        this._noWinAnimation.visible = false;

        this._animation = new SpinePlayer("fsOutro", false);
        this._animation.position.set(360);
        this._animation.visible = false;

        this._spinsPlayedText = new PIXI.Text(PunkRocker.apiPlugin.translations.translate("Spins Played:"), this._spinsPlayedTextStyle);
        this._spinsPlayedText.name = "_spinsPlayedText";
        this._spinsPlayedText.anchor.set(0);
        this._spinsPlayedText.position.set(0, 0);

        this._spinsPlayedNr = new PIXI.Text("22", this._spinsPlayedTextStyle);
        this._spinsPlayedNr.name = "_spinsPlayedNr";
        this._spinsPlayedNr.anchor.set(0);
        this._spinsPlayedNr.position.set(this._spinsPlayedText.width, 0);

        this._spinsPlayedContainer = new PIXI.Sprite();
        this._spinsPlayedContainer.addChild(this._spinsPlayedText, this._spinsPlayedNr);
        this._spinsPlayedContainer.visible = false;

        this._totalWin = new BitmapText("0", {
            fontName: GameAssets.BITMAP_FONT_WIN_COUNT_UP_LOW,
            fontSize: 68,
            letterSpacing: 5
        });
        this._totalWin.name = "totalWin";
        this._totalWin.anchor = new PIXI.Point(0.5, 0.5);
        this._totalWin.position.set(360, 300);
        this._totalWin.visible = false;

        this._continueButton = new ContinueButton();
        this._continueButton.addClickCallback(() => this.clickCallback());
        this._continueButton.pivot.set(this._continueButton.width * 0.5, this._continueButton.height * 0.5);
        this._continueButton.enable(false);
        this._continueButton.visible = false;

        this.addChild(this._noWinAnimation, this._animation, this._spinsPlayedContainer, this._totalWin, this._continueButton);

        this.onResize();
    }

    private clickCallback(): void {
        PunkRocker.gameLogo.setBGAlpha(0.5, 0, 0.5);
        SoundManager.playSoundEffect(SoundConfig.FS_OUTRO_BUTTON);

        SoundManager.stopMainAmbiance();
        SoundManager.playAmbientLoop(GameMode.NORMAL);

        this._continueButton.enable(false);

        this._timeline = gsap.timeline();

        this._timeline.to([this._animation, this._spinsPlayedContainer, this._totalWin, this._continueButton], {
            duration: 0.2,
            alpha: 0
        });
        this._timeline.add(PunkRocker.screenDimmer.fadeOut(), "<");

        this._timeline.add(() => {
            this._animation.visible = this._spinsPlayedContainer.visible = this._totalWin.visible = this._continueButton.visible = false;

            Helper.setTimeline(this._loop);
            PunkRocker.screenDimmer.hide();

            //PosterManager.instance.playOutroTrain(true);
            /*PunkRocker.reelEngineManager.AllReels.forEach((r, rid) => {
                r.AllWindowSymbols.forEach(s => {
                    if (s.isScatter) {
                        const sPos: (number | null)[] = this._scatterPos.map((num, index) => num > -1 ? index : null).filter(index => index !== null);
                        if (sPos.indexOf(rid) < 4) { //max 4 allowed
                            PunkRocker.jumpWilds.startShowJW(sPos.indexOf(rid), rid, s.posID[0]);
                        }
                    }
                });
            });*/

            PunkRocker.jumpWilds.makeAllDead();

            PunkRocker.background.fromBonusToMain();
            PunkRocker.noService.toggleServiceBoard(false);
        });

        this._timeline.to(this, {
            duration: SpinePlayer.fetchDuration(BonusGameEndState.BG_END, "bonusGameToMainGame")
        });

        PunkRocker.trainsManager.topTrain.showGlowPositions([0, 1, 2, 3, 4, 5], false, 0.5);
        PunkRocker.trainsManager.bottomTrain.showGlowPositions([0, 1, 2, 3, 4, 5], false, 0.5);
    }
}
